/**
 * 买 N 送 M 活动规则：计算购买资格、赠品额度、领取状态和购物车清理数量。
 * 所有规则只读取传入数据，不直接修改购物车、不发送请求，也不操作 DOM。
 * 购物车数据约定：cart.items 为商品行数组，memberships 为商品 ID 到集合 ID 数组的映射。
 * 赠品属性约定：_km_bogo_activity 记录活动 ID，_km_bogo_anchor 记录来源商品标识。
 */
/**
 * 兼容浏览器与 Node.js 的模块入口。
 * @param {Window|Object} root 浏览器 window 或当前全局对象。
 * @param {Function} factory 无入参的规则工厂，返回规则接口对象。
 * @returns {void} 浏览器中挂载 KMCartBogoRules，Node.js 中写入 module.exports。
 */
(function (root, factory) {
  const rules = factory();
  if (typeof module === 'object' && module.exports) module.exports = rules;
  else root.KMCartBogoRules = rules;
// 模块入口功能结束；下方工厂函数创建具体规则。
/**
 * 创建规则函数及公开接口。
 * 入参：无。
 * @returns {Object} 活动匹配、额度计算、赠品筛选、加购校验和清理规则接口。
 */
})(typeof window !== 'undefined' ? window : globalThis, function () {
  /**
   * 将后台填写的 SKU 文本转换为统一格式的列表。
   * @param {string} [value] 以换行或英文逗号分隔的 SKU 文本。
   * @returns {string[]} 去除首尾空格、转为大写并排除空项的 SKU 数组；不去重。
   */
  const skus = value => String(value || '').split(/[\n,\r]+/).map(sku => sku.trim().toUpperCase()).filter(Boolean);
  // skus 功能结束。

  /**
   * 判断商品行是否属于买赠、满额赠或受支持的旧赠品。
   * @param {Object} item 购物车商品行，properties 可缺省。
   * @returns {boolean} 是否为赠品；赠品不能再次作为购买资格来源。
   */
  const isGift = item => {
    const p = item.properties || {};
    return Boolean(p._km_bogo_activity || String(p._km_cart_gift) === 'true'
      || String(p._uc_is_gift) === '1' || p._uc_pick_section);
  };
  // isGift 功能结束。

  /**
   * 判断单个购买商品是否匹配活动的集合和可选 SKU 限制。
   * @param {Object} activity 活动配置，包含 collectionId、buySkus。
   * @param {Object} item 待判断的购物车商品行。
   * @param {Object<string, string[]>} memberships 商品 ID 到集合 ID 数组的映射。
   * @returns {boolean} 是否匹配；此处不检查购买数量或剩余领取额度。
   */
  const qualifies = (activity, item, memberships) => !isGift(item)
    && (memberships[String(item.product_id)] || []).includes(String(activity.collectionId))
    && (!skus(activity.buySkus).length || skus(activity.buySkus).includes(String(item.sku || '').trim().toUpperCase()));
  // qualifies 功能结束。

  /**
   * 查找当前商品匹配的活动；多个活动匹配时使用配置顺序靠前的一项。
   * @param {Object[]} activities 已启用的活动配置列表。
   * @param {Object|null|undefined} item 当前购买商品行。
   * @param {Object<string, string[]>} memberships 商品所属集合映射。
   * @returns {Object|null} 首个匹配活动；没有商品或没有匹配时返回 null。
   */
  const match = (activities, item, memberships) => item
    ? activities.find(activity => qualifies(activity, item, memberships)) || null : null;
  // match 功能结束。

  /**
   * 生成来源标识，绑定购买商品与它领取的赠品。
   * @param {Object} item 购物车购买商品行，包含 product_id 和 variant_id 或 id。
   * @returns {string} “商品 ID:规格 ID”；不依赖可能随折扣变化的购物车行 key。
   */
  const anchorId = item => `${item.product_id}:${item.variant_id || item.id}`;
  // anchorId 功能结束。

  /**
   * 判断是否为按件赠送的买一赠一活动。
   * @param {Object} activity 活动配置，缺省 N、M 均按 1 处理。
   * @returns {boolean} 买一赠一时为 true；该活动始终按购买件数赠送并允许同款赠品。
   */
  const isBogo = activity => Math.max(1, Number(activity.buyQuantity) || 1) === 1
    && Math.max(1, Number(activity.giftQuantity) || 1) === 1;
  // isBogo 功能结束。

  /**
   * 判断是否允许重复领取同一赠品规格。
   * @param {Object} activity 活动配置。
   * @returns {boolean} 买一赠一始终允许，其他 N/M 活动遵循后台设置。
   */
  const allowsSameGift = activity => isBogo(activity) || activity.allowSameGift;
  // allowsSameGift 功能结束。

  /**
   * 计算可领取赠品的总额度，尚未扣除已经领取的数量。
   * @param {Object} activity 活动配置，使用 buyQuantity、giftQuantity、repeat、maxSets。
   * @param {Object} cart 购物车快照，包含 items 数组。
   * @param {Object<string, string[]>} memberships 商品所属集合映射。
   * @param {Object} [anchor] 来源商品；传入时仅统计同商品同规格，否则合计活动内购买数量。
   * @returns {number} 可领取的赠品件数；未满足购买门槛时为 0。
   * 买一赠一按来源购买件数计算；其他 N/M 活动才应用 repeat 和 maxSets。
   */
  const allowance = (activity, cart, memberships, anchor) => {
    // 先排除赠品与不符合条件的购买商品，再根据可选来源标识累计购买件数。
    const quantity = cart.items.filter(item => qualifies(activity, item, memberships) && (!anchor || anchorId(item) === anchorId(anchor)))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    // 买一赠一直接按来源购买件数赠送，兼容旧配置中的单组上限和关闭重复赠送设置。
    if (isBogo(activity)) return quantity;
    // 其他活动每满 N 件算一组；不重复赠送时最多一组，重复时受 maxSets 限制，最后乘以 M。
    const groups = Math.floor(quantity / Math.max(1, Number(activity.buyQuantity) || 1));
    return Math.min(groups, activity.repeat ? Math.max(1, Number(activity.maxSets) || 1) : 1)
      * Math.max(1, Number(activity.giftQuantity) || 1);
  };
  // allowance 功能结束。

  /**
   * 获取指定活动、指定来源已经领取的赠品行。
   * @param {Object} activity 活动配置，通过 id 匹配赠品属性。
   * @param {Object} cart 购物车快照，包含 items 数组。
   * @param {Object} [anchor] 来源商品；未传时返回当前活动全部赠品。
   * @returns {Object[]} 匹配的商品行数组；返回原对象引用，不修改商品行。
   * 兼容节点：旧赠品没有来源属性时，也计入指定来源的领取状态，保守占用额度。
   */
  const claimed = (activity, cart, anchor) => cart.items.filter(item => item.properties?._km_bogo_activity === activity.id
    && (!anchor || item.properties._km_bogo_anchor === anchorId(anchor) || !item.properties._km_bogo_anchor));
  // claimed 功能结束。

  /**
   * 筛选赠品商品中可领取的规格。
   * @param {Object} activity 活动配置，giftSkus 为空时不限制 SKU。
   * @param {Object} product 赠品商品配置，variants 可缺省。
   * @returns {Object[]} 可售且符合赠品 SKU 限制的规格数组。
   */
  const variants = (activity, product) => (product.variants || []).filter(variant => variant.available
    && (!skus(activity.giftSkus).length || skus(activity.giftSkus).includes(String(variant.sku || '').trim().toUpperCase())));
  // variants 功能结束。

  /**
   * 检查指定赠品规格是否还能领取一件。
   * @param {Object} activity 活动配置，包含赠品池、重复领取设置和数量规则。
   * @param {Object} cart 购物车快照，包含 items 数组。
   * @param {Object<string, string[]>} memberships 商品所属集合映射。
   * @param {string|number} variantId 待领取的赠品规格 ID。
   * @param {Object} [anchor] 来源商品，用于隔离不同来源的领取状态与额度。
   * @returns {boolean} 规格有效、符合重复领取限制且尚有额度时返回 true。
   */
  const canAdd = (activity, cart, memberships, variantId, anchor) => {
    // 关键校验顺序：赠品池及可售规格 → 重复规格限制 → 已领取总量是否达到额度。
    if (!activity.products.some(product => variants(activity, product).some(variant => String(variant.id) === String(variantId)))) return false;
    const gifts = claimed(activity, cart, anchor);
    if (!allowsSameGift(activity) && gifts.some(item => String(item.variant_id || item.id) === String(variantId))) return false;
    return gifts.reduce((sum, item) => sum + Number(item.quantity || 0), 0) < allowance(activity, cart, memberships, anchor);
  };
  // canAdd 功能结束。

  /**
   * 计算失效赠品或超额赠品所需的数量更新，不直接发送购物车请求。
   * @param {Object[]} activities 当前已启用的活动列表。
   * @param {Object} cart 购物车快照，包含 items 数组。
   * @param {Object<string, string[]>} memberships 商品所属集合映射，应由已就绪的商品信息生成。
   * @returns {Object<string, number>} 行 key 到目标数量的映射；0 表示删除，无变更返回空对象。
   */
  const updates = (activities, cart, memberships) => {
    // 初始活动级额度用于兼容旧的无来源赠品；新赠品使用“活动 + 来源”单独分配额度。
    const remaining = new Map(activities.map(activity => [activity.id, allowance(activity, cart, memberships)]));
    const seen = new Set();
    const changes = {};
    cart.items.filter(item => item.properties?._km_bogo_activity).forEach(item => {
      const id = item.properties._km_bogo_activity;
      const activity = activities.find(activity => activity.id === id);
      // 在最新购物车中查找来源；来源删除、失去购买资格或活动不存在时，对应额度归零。
      const source = item.properties._km_bogo_anchor;
      const anchor = source && cart.items.find(line => !isGift(line) && anchorId(line) === source);
      const budget = source ? `${id}:${source}` : id;
      if (source && !remaining.has(budget)) remaining.set(budget, activity && anchor ? allowance(activity, cart, memberships, anchor) : 0);
      const variantId = String(item.variant_id || item.id);
      // 活动池已移除的规格、不可售规格和不再匹配 SKU 限制的规格均需要清理。
      const valid = activity?.products.some(product => variants(activity, product).some(variant => String(variant.id) === variantId));
      const token = `${budget}:${variantId}`;
      // 按购物车行顺序保留赠品：买一赠一允许同款，其余活动遵循重复限制，均受剩余额度限制。
      const sameGift = activity && allowsSameGift(activity);
      const quantity = !valid || !sameGift && seen.has(token) ? 0
        : Math.min(Number(item.quantity || 0), remaining.get(budget) || 0, sameGift ? Infinity : 1);
      // 扣减已占用额度并记录规格；仅把目标数量确实变化的行加入更新结果。
      remaining.set(budget, (remaining.get(budget) || 0) - quantity);
      seen.add(token);
      if (quantity !== Number(item.quantity)) changes[item.key] = quantity;
    });
    return changes;
  };
  // updates 功能结束。

  // 返回公开接口；SKU 标准化与单商品资格判断仅供本模块内部调用。
  return {match, allowance, claimed, variants, canAdd, updates, isGift, anchorId};
});
// 规则工厂及购物车 BOGO 规则模块功能结束。
